import { Navigate, useLocation } from 'react-router-dom';
import { useGetMeQuery } from '@api/authApi';
import { Loader2 } from 'lucide-react';

/**
 * ProtectedRoute — Checks auth state before rendering children.
 *
 * Flow:
 * 1. No token → immediate redirect to /login
 * 2. Has token → call GET /auth/me to validate
 *    - Loading → show spinner
 *    - Success → render children
 *    - 401/error → clear token, redirect to /login
 *
 * Saves current location so login can redirect back after success.
 */
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  const hasToken = !!localStorage.getItem('accessToken');

  const { data, isLoading, error } = useGetMeQuery(undefined, {
    skip: !hasToken,
  });

  // No token at all — redirect immediately
  if (!hasToken) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Validating token / fetching user
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-surface-50">
        <div className="flex flex-col items-center gap-4 animate-fade-in">
          <div className="relative">
            <div className="w-12 h-12 rounded-2xl bg-primary-50 flex items-center justify-center">
              <Loader2 size={24} className="text-primary-600 animate-spin" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-surface-700">Loading workspace...</p>
            <p className="text-xs text-surface-500 mt-0.5">Verifying your session</p>
          </div>
        </div>
      </div>
    );
  }

  // Token invalid / expired / API error
  if (error || !data?.data) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('activeWorkspaceId');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

export default ProtectedRoute;
